"use client";

import Image from "@/components/shared/image";
import Link from "@/components/shared/link";
import { useAnalytics } from "@/lib/analytics";

// Determine if on landing page and set appropriate hrefs
const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
const isLandingPage = pathname === "/";

const PRODUCT_LINKS = [
  { label: "How it works", href: isLandingPage ? "#process" : "https://zapify.pro/#process" },
  { label: "Features", href: isLandingPage ? "#features" : "https://zapify.pro/#features" },
  { label: "Pricing", href: "/pricing" },
  { label: "Free Tools", href: "/tools" },
  { label: "FAQ", href: isLandingPage ? "#faq" : "https://zapify.pro/#faq" },
] as const;

const COMPANY_LINKS = [
  { label: "Terms of Service", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Refund Policy", href: "/refund-policy" },
  { label: "Contact Us", href: "/contact" },
  { label: "Referral Program", href: "/referral" },
] as const;

const USE_CASES_LINKS = [
  { label: "All Use Cases", href: "/use-case" },
  { label: "Send Links in DMs", href: "/use-case/instagram-dm-link-automation" },
  { label: "Auto-Respond Comments", href: "/use-case/instagram-comment-dm-automation" },
  { label: "Collect Emails", href: "/use-case/instagram-dm-email-collection" },
  { label: "Story Replies", href: "/use-case/instagram-story-reply-automation" },
] as const;

const SOCIAL_LINKS = [
  { label: "Instagram", username: "@zapify.pro", href: "https://www.instagram.com/Zapify.pro", src: "/social/instagram_-con.webp" },
  { label: "TikTok", username: "@zapifypro", href: "https://www.tiktok.com/@zapifypro", src: "/social/tiktok-icon.webp" },
  { label: "YouTube", username: "@ZapifyPro", href: "https://www.youtube.com/@ZapifyPro", src: "/social/youtube-icon.png" },
  { label: "Facebook", username: "@ZapifyPro", href: "https://www.facebook.com/ZapifyPro", src: "/social/facebook-icon.png" },
  { label: "Threads", username: "@zapify.pro", href: "https://www.threads.com/@zapify.pro", src: "/social/thread-icon.png" },
  { label: "X", username: "@ZapifyPro", href: "https://x.com/ZapifyPro", src: "/social/x-icon.png" },
  { label: "LinkedIn", username: "@zapifypro", href: "https://www.linkedin.com/company/zapifypro/", src: "/social/linkedin-icon.png" },
  { label: "Reddit", username: "@ZapifyPro", href: "https://www.reddit.com/r/ZapifyPro", src: "/social/reddit-icon.png" },
] as const;

const SOCIAL_ROWS = [SOCIAL_LINKS.slice(0, 4), SOCIAL_LINKS.slice(4, 8)] as const;

function FooterList({
  title,
  links,
  onLinkClick,
}: {
  title: string;
  links: ReadonlyArray<{ label: string; href: string }>;
  onLinkClick: (href: string) => void;
}) {
  return (
    <div>
      <p className="text-[13px] font-bold uppercase tracking-wide text-foreground sm:text-sm">{title}</p>
      <ul className="mt-2 space-y-1 sm:mt-3 sm:space-y-2.5">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              href={link.href}
              onClick={() => onLinkClick(link.href)}
              className="text-[13px] text-muted-foreground transition-colors hover:text-foreground sm:text-sm"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  const { track } = useAnalytics();

  const handleFooterLinkClick = (href: string) => {
    if (href.startsWith("#")) {
      track("footer_link_clicked", {
        link: href,
        target: href.slice(1),
        section: "footer",
      });
      return;
    }

    track("footer_link_clicked", {
      link: href,
      section: "footer",
      isExternal: true,
    });
  };

  const handleSocialClick = (platform: string, url: string) => {
    track("social_media_clicked", {
      platform,
      url,
      section: "footer",
    });
  };

  return (
    <footer className="relative w-full">
      <div className="relative z-0 w-full bg-background px-4 pb-0 sm:px-6 sm:pb-6 lg:px-8">
        <div className="mx-auto w-full max-w-7xl pt-3.5 pb-0 sm:py-6 lg:py-7">
          <div className="grid gap-x-2 gap-y-4 pb-3 sm:gap-5 sm:pb-4 lg:grid-cols-[1fr_1.45fr]">
            <div className="text-center lg:text-left">
              <h3 className="text-4xl font-extrabold tracking-tight text-foreground">Zapify</h3>
              <p className="mx-auto mt-2 max-w-sm text-[1.1rem] leading-tight text-foreground/90 sm:text-[1.55rem] lg:mx-0">
                Instagram DM automation for creators, brands, and agencies that want faster replies, better lead capture, and fewer missed conversations.
              </p>
              <div className="mt-2 hidden space-y-1.5 sm:mt-4 sm:block sm:space-y-2.5">
                {SOCIAL_ROWS.map((row, rowIdx) => (
                  <div key={rowIdx} className="flex flex-nowrap justify-center gap-2.5 overflow-visible lg:justify-start">
                    {row.map((item) => (
                      <a
                        key={item.href}
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={item.label}
                        onClick={() => handleSocialClick(item.label, item.href)}
                        className="group inline-flex h-10 w-10 items-center justify-center gap-0 overflow-hidden rounded-full bg-white px-0 ring-1 ring-black/8 transition-all duration-300 ease-out hover:w-36 hover:justify-start hover:gap-2 hover:rounded-2xl hover:px-3 dark:bg-zinc-900 dark:ring-white/15 sm:h-12 sm:w-12"
                        title={item.label}
                      >
                        <span className="relative size-6 shrink-0 translate-x-[1.5px] sm:size-8">
                          <Image
                            src={item.src}
                            alt=""
                            width={32}
                            height={32}
                            className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full object-contain object-center sm:h-8 sm:w-8"
                          />
                        </span>
                        <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-medium text-foreground opacity-0 transition-all duration-300 ease-out group-hover:max-w-[95px] group-hover:opacity-100 dark:text-zinc-100">
                          {item.username}
                        </span>
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div
              className="relative mx-auto hidden aspect-[2/1] w-[min(7rem,78vw)] shrink-0 overflow-hidden rounded-md sm:mx-auto sm:block sm:aspect-auto sm:h-auto sm:w-full sm:max-h-none sm:max-w-[98%] sm:shrink sm:rounded-none lg:max-w-[97%]"
            >
              <Image
                src="/footer_top_right_new.webp?v=20260515"
                alt="Creators visual"
                width={1400}
                height={700}
                className="absolute inset-0 h-full w-full object-cover object-center dark:hidden sm:static sm:inset-auto sm:h-auto sm:w-full"
                unoptimized
              />
              <Image
                src="/footer_top_right_new_dark.webp?v=20260515"
                alt="Creators visual dark"
                width={1400}
                height={700}
                className="absolute inset-0 hidden h-full w-full object-cover object-center dark:block sm:static sm:inset-auto sm:h-auto sm:w-full"
                unoptimized
              />
            </div>
            <div className="mt-0 flex max-w-full flex-nowrap justify-center gap-1 overflow-visible px-0.5 pb-2 sm:hidden">
              {SOCIAL_LINKS.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={item.label}
                  onClick={() => handleSocialClick(item.label, item.href)}
                  className="group relative z-0 inline-flex h-9 w-9 shrink-0 items-center justify-center gap-0 overflow-hidden rounded-full bg-white px-0 ring-1 ring-black/8 transition-all duration-300 ease-out hover:z-20 hover:w-[7.5rem] hover:justify-start hover:gap-1.5 hover:rounded-2xl hover:px-2 hover:shadow-md active:scale-[0.98] dark:bg-zinc-900 dark:ring-white/15"
                  title={item.label}
                >
                  <span className="relative size-5 shrink-0 translate-x-[1px]">
                    <Image
                      src={item.src}
                      alt=""
                      width={28}
                      height={28}
                      className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full object-contain object-center"
                    />
                  </span>
                  <span className="max-w-0 overflow-hidden whitespace-nowrap text-[11px] font-medium text-foreground opacity-0 transition-all duration-300 ease-out group-hover:max-w-[5.5rem] group-hover:opacity-100 dark:text-zinc-100">
                    {item.username}
                  </span>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-1 grid grid-cols-2 gap-x-2.5 gap-y-3.5 border-b border-border/70 pb-3 sm:mt-2 sm:gap-5 sm:gap-y-5 sm:pb-5 lg:grid-cols-[1fr_1fr_1fr_1.25fr]">
            <div className="text-left">
              <FooterList title="Product" links={PRODUCT_LINKS} onLinkClick={handleFooterLinkClick} />
            </div>
            <div className="text-left">
              <FooterList title="Company" links={COMPANY_LINKS} onLinkClick={handleFooterLinkClick} />
            </div>
            <div className="text-left">
              <FooterList title="Use Cases" links={USE_CASES_LINKS} onLinkClick={handleFooterLinkClick} />
            </div>
            <div className="flex flex-col items-start text-left">
              <p className="text-2xl font-bold text-foreground lg:text-left">
                Built with <span className="gradient-text">❤</span>
                <br />
                for creators
              </p>
              <div className="mt-1.5 w-full max-w-[9rem] sm:mt-4 sm:max-w-[15.5rem] lg:max-w-[17.5rem]">
                <Image
                  src="/stripe.png"
                  alt="Stripe"
                  width={200}
                  height={56}
                  className="h-auto w-full max-w-full object-contain object-left opacity-90 sm:max-w-none"
                />
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-col items-center gap-2 text-center text-xs text-muted-foreground sm:mt-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:text-left">
            <p>© {new Date().getFullYear()} Zapify. All rights reserved.</p>
            <div className="flex items-center justify-center gap-3 sm:gap-4">
              <Link href="/terms" onClick={() => handleFooterLinkClick("/terms")} className="transition-colors hover:text-foreground">
                Terms & Conditions
              </Link>
              <Link href="/contact" onClick={() => handleFooterLinkClick("/contact")} className="transition-colors hover:text-foreground">
                Contact
              </Link>
              <Link href="/privacy" onClick={() => handleFooterLinkClick("/privacy")} className="transition-colors hover:text-foreground">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
